import type { MouseEventHandler } from 'react'
import { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage } from 'react-intl'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import ModalContext from 'contexts/ModalContext'
import LoyaltyTierMessageBanner from 'loyalty/components/LoyaltyTierMessageBanner'
import TierProgressionInfoModal from './TierProgressionInfoModal'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineLuxuryEscapesMonogramIcon from 'LuxKit/Icons/line/LineLuxuryEscapesMonogramIcon'
import IconButton from 'LuxKit/Button/IconButton'
import TextLink from 'LuxKit/TextLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const LearnMoreLink = styled(TextLink)`
  padding-inline-end: ${rem(8)};
`

interface Props {
  highestUnlockableTier: App.LoyaltyTier
  highestUnlockableTierName: string
  onBookNow?: () => void
  onAddToCart?: () => void
  checkoutDate?: string
}

function RateTierProgressionCallOutBanner({
  highestUnlockableTier,
  highestUnlockableTierName,
  onBookNow,
  onAddToCart,
  checkoutDate,
}: Props) {
  const showModal = useContext(ModalContext)

  const handleOpenModal = useCallback<MouseEventHandler>((event) => {
    event.preventDefault()
    showModal(<TierProgressionInfoModal
      tiers={[highestUnlockableTier]}
      onBookNow={onBookNow}
      onAddToCart={onAddToCart}
      checkoutDate={checkoutDate}
    />)
  }, [showModal, highestUnlockableTier, onBookNow, onAddToCart, checkoutDate])

  return (
    <ProductPaletteProvider palette={`tier-${highestUnlockableTier}`}>
      <CSSBreakpoint min="tablet">
        <LoyaltyTierMessageBanner
          tier={highestUnlockableTier}
          endContent={
            <LearnMoreLink size="medium" onClick={handleOpenModal}>
              <FormattedMessage
                id="47I-Dg-m-UnGF5JPVVdV"
                defaultMessage="Learn more"
                description="Link to open tier progression info modal on per-package banner"
              />
            </LearnMoreLink>
          }
        >
          <FormattedMessage
            id="YmHifWkKXbDaNf5SAxmL"
            defaultMessage="Unlock {tierName} Status when you book this package"
            description="Message shown on per-package banner indicating tier unlock"
            values={{ tierName: highestUnlockableTierName }}
          />
        </LoyaltyTierMessageBanner>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <LoyaltyTierMessageBanner
          tier={highestUnlockableTier}
          density="comfortable"
          icon={<LineLuxuryEscapesMonogramIcon />}
          endContent={
            <IconButton
              kind="tertiary"
              variant="dark"
              size="small"
              onClick={handleOpenModal}
            >
              <LineInfoCircleIcon />
            </IconButton>
          }
        >
          <FormattedMessage
            id="6Qfgx9uEH141hzlsnKAX"
            defaultMessage="Unlock {tierName} Status"
            description="Tier progression banner text showing which loyalty tier the user can unlock by booking this rate"
            values={{ tierName: highestUnlockableTierName }}
          />
        </LoyaltyTierMessageBanner>
      </CSSBreakpoint>
    </ProductPaletteProvider>
  )
}

export default RateTierProgressionCallOutBanner
